/**
 * Per-scope coverage and per-scope verb overrides — the content behind the
 * card's Customize disclosure, in the ladder's own row grammar:
 *
 *   Turn a pipeline off, or make an exception for one. Everything else
 *   follows the level above.
 *   [mark] Contacts & companies – Follows Most updates              ›
 *   [mark] Deals – More than Most updates – merge duplicates        ›
 *            [mark] Mark as Closed Won
 *            [mark] Move back a stage
 *            [mark] Assign to teammates
 *            Beyond Most updates
 *            [mark] Merge duplicates
 *            [mark] Change details someone entered
 *   [mark] Tasks – Off                                              ›
 *
 * The panel's job is to make "nothing unusual is set" read as calm: every
 * scope row carries a status sentence, and a scope's verbs are listed in
 * level order with a divider before the ones that would be an exception.
 * A scope row's mark grants or removes the scope; its disclosure opens the
 * verbs, each a mark of its own, disabled while the scope is off.
 */

import { Fragment, useCallback, useMemo } from 'react';

import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { selectTerminalStageNames } from '@/lib/crm/pipeline/utils/selectTerminalStageNames';

import {
  ASSIGN_LABEL,
  BEYOND_LEVEL_PREFIX,
  CONTACTS_SCOPE_LABEL,
  EDIT_LABEL,
  joinWithAnd,
  lowerFirst,
  MERGE_LABEL,
  MOVE_BACK_LABEL,
  NEW_PIPELINES_NOTE,
  SCOPE_DIFFERS_PREFIX,
  SCOPE_FOLLOWS_PREFIX,
  SCOPE_LESS_THAN_PREFIX,
  SCOPE_MORE_THAN_PREFIX,
  SCOPE_OFF,
  STOP_LABELS,
  TOAST_OFF,
  TOAST_ON,
  TUNE_INTRO,
} from '../agent-autonomy-copy';
import type {
  AutonomyDerivation,
  AutonomyGrantState,
  AutonomyStop,
  ContactsVerb,
  PipelineVerb,
} from '../autonomyPresets';
import {
  CONTACTS_VERB_STOPS,
  contactsVerbsAboveLevel,
  contactsVerbsBelowLevel,
  PIPELINE_VERB_STOPS,
  pipelineVerbsAboveLevel,
  pipelineVerbsBelowLevel,
} from '../autonomyPresets';
import type { AgentCaseType, PipelineGrant } from '../hooks/useAgentSettings';
import { formatMarkCompletedLabel } from '../markCompletedLabel';
import { DisclosureRow, LeafRow, ROW_INDENT } from './AutonomyRows';

function markOf(on: boolean) {
  return on ? 'checked' : 'unchecked';
}

/** The status sentence on a scope row: what it carries beyond the level
 *  (`extras`) and what it is missing from it (`missing`). */
function describeScope(
  granted: boolean,
  level: AutonomyStop,
  extras: readonly string[],
  missing: readonly string[]
): string {
  if (!granted) {
    return SCOPE_OFF;
  }
  const stop = STOP_LABELS[level];
  const on = joinWithAnd(extras.map(lowerFirst));
  const off = joinWithAnd(missing.map(lowerFirst));
  if (extras.length > 0 && missing.length > 0) {
    return `${SCOPE_DIFFERS_PREFIX} ${stop} – ${on} ${TOAST_ON}, ${off} ${TOAST_OFF}`;
  }
  if (extras.length > 0) {
    return `${SCOPE_MORE_THAN_PREFIX} ${stop} – ${on}`;
  }
  if (missing.length > 0) {
    return `${SCOPE_LESS_THAN_PREFIX} ${stop} – ${off} ${TOAST_OFF}`;
  }
  return `${SCOPE_FOLLOWS_PREFIX} ${stop}`;
}

/** Verb labels for a list of verbs, from the row specs. */
function labelsOf<V extends string>(
  verbs: readonly V[],
  specs: readonly VerbSpec<V>[]
): string[] {
  return verbs.map(v => specs.find(s => s.verb === v)?.label ?? v);
}

// ---------------------------------------------------------------------------
// Verbs
// ---------------------------------------------------------------------------

interface VerbSpec<V extends string> {
  verb: V;
  label: string;
  stop: AutonomyStop;
  on: boolean;
}

interface VerbRowProps<V extends string> {
  verb: V;
  label: string;
  scopeLabel: string;
  on: boolean;
  isDisabled: boolean;
  onToggle(verb: V, on: boolean): void;
}

function VerbRow<V extends string>({
  verb,
  label,
  scopeLabel,
  on,
  isDisabled,
  onToggle,
}: VerbRowProps<V>) {
  const handleMark = useCallback(
    (next: boolean) => onToggle(verb, next),
    [onToggle, verb]
  );
  return (
    <li>
      <LeafRow
        mark={markOf(on)}
        markLabel={`${label} – ${scopeLabel}`}
        isDisabled={isDisabled}
        onMark={handleMark}
        label={label}
      />
    </li>
  );
}

interface VerbListProps<V extends string> {
  verbs: readonly VerbSpec<V>[];
  level: AutonomyStop;
  scopeLabel: string;
  isDisabled: boolean;
  onToggle(verb: V, on: boolean): void;
}

/** A scope's verbs in level order, with a divider before the first one the
 *  level does not include — so an unchecked mark reads as "not asked for",
 *  not "forgotten". */
function VerbList<V extends string>({
  verbs,
  level,
  scopeLabel,
  isDisabled,
  onToggle,
}: VerbListProps<V>) {
  const ordered = useMemo(
    () => [...verbs].sort((a, b) => a.stop - b.stop),
    [verbs]
  );
  const firstBeyond = ordered.findIndex(v => v.stop > level);
  return (
    <ul>
      {ordered.map((spec, index) => (
        <Fragment key={spec.verb}>
          {index === firstBeyond && (
            <li
              className={`mt-1.5 mb-0.5 text-xs text-fg-tertiary ${ROW_INDENT}`}
            >
              {`${BEYOND_LEVEL_PREFIX} ${STOP_LABELS[level]}`}
            </li>
          )}
          <VerbRow
            verb={spec.verb}
            label={spec.label}
            scopeLabel={scopeLabel}
            on={spec.on}
            isDisabled={isDisabled}
            onToggle={onToggle}
          />
        </Fragment>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Scopes
// ---------------------------------------------------------------------------

interface ContactsScopeRowProps {
  grants: AutonomyGrantState;
  level: AutonomyStop;
  isDisabled: boolean;
  onScope(on: boolean): void;
  onVerb(verb: ContactsVerb, on: boolean): void;
}

function ContactsScopeRow({
  grants,
  level,
  isDisabled,
  onScope,
  onVerb,
}: ContactsScopeRowProps) {
  const granted = grants.addContacts;
  const verbs = useMemo<VerbSpec<ContactsVerb>[]>(
    () => [
      {
        verb: 'mergeContacts',
        label: MERGE_LABEL,
        stop: CONTACTS_VERB_STOPS.mergeContacts,
        on: grants.mergeContacts,
      },
      {
        verb: 'assignContacts',
        label: ASSIGN_LABEL,
        stop: CONTACTS_VERB_STOPS.assignContacts,
        on: grants.assignContacts,
      },
      {
        verb: 'editContacts',
        label: EDIT_LABEL,
        stop: CONTACTS_VERB_STOPS.editContacts,
        on: grants.editContacts,
      },
    ],
    [grants.mergeContacts, grants.assignContacts, grants.editContacts]
  );
  const extras = granted
    ? labelsOf(contactsVerbsAboveLevel(grants, level), verbs)
    : [];
  const missing = granted
    ? labelsOf(contactsVerbsBelowLevel(grants, level), verbs)
    : [];
  return (
    <li>
      <DisclosureRow
        mark={markOf(granted)}
        markLabel={CONTACTS_SCOPE_LABEL}
        isDisabled={isDisabled}
        onMark={onScope}
        title={CONTACTS_SCOPE_LABEL}
        summary={describeScope(granted, level, extras, missing)}
      >
        <VerbList
          verbs={verbs}
          level={level}
          scopeLabel={CONTACTS_SCOPE_LABEL}
          isDisabled={isDisabled || !granted}
          onToggle={onVerb}
        />
      </DisclosureRow>
    </li>
  );
}

const OFF_GRANT: PipelineGrant = {
  markCompleted: false,
  moveBackward: false,
  mergeDuplicates: false,
  assignToTeammates: false,
  editEnteredDetails: false,
};

interface PipelineScopeRowProps {
  caseType: AgentCaseType;
  grant: PipelineGrant | undefined;
  terminalNames: string[];
  level: AutonomyStop;
  isDisabled: boolean;
  onScope(entityTypeName: string, on: boolean): void;
  onVerb(entityTypeName: string, verb: PipelineVerb, on: boolean): void;
}

function PipelineScopeRow({
  caseType,
  grant,
  terminalNames,
  level,
  isDisabled,
  onScope,
  onVerb,
}: PipelineScopeRowProps) {
  const { entityTypeName, label } = caseType;
  const granted = grant !== undefined;
  const bits = grant ?? OFF_GRANT;
  const handleScope = useCallback(
    (on: boolean) => onScope(entityTypeName, on),
    [onScope, entityTypeName]
  );
  const handleVerb = useCallback(
    (verb: PipelineVerb, on: boolean) => onVerb(entityTypeName, verb, on),
    [onVerb, entityTypeName]
  );
  const markCompletedLabel = formatMarkCompletedLabel(terminalNames);
  const verbs = useMemo<VerbSpec<PipelineVerb>[]>(
    () => [
      {
        verb: 'markCompleted',
        label: markCompletedLabel,
        stop: PIPELINE_VERB_STOPS.markCompleted,
        on: bits.markCompleted,
      },
      {
        verb: 'moveBackward',
        label: MOVE_BACK_LABEL,
        stop: PIPELINE_VERB_STOPS.moveBackward,
        on: bits.moveBackward,
      },
      {
        verb: 'mergeDuplicates',
        label: MERGE_LABEL,
        stop: PIPELINE_VERB_STOPS.mergeDuplicates,
        on: bits.mergeDuplicates,
      },
      {
        verb: 'assignToTeammates',
        label: ASSIGN_LABEL,
        stop: PIPELINE_VERB_STOPS.assignToTeammates,
        on: bits.assignToTeammates,
      },
      {
        verb: 'editEnteredDetails',
        label: EDIT_LABEL,
        stop: PIPELINE_VERB_STOPS.editEnteredDetails,
        on: bits.editEnteredDetails,
      },
    ],
    [markCompletedLabel, bits]
  );
  const extras = grant
    ? labelsOf(pipelineVerbsAboveLevel(grant, level), verbs)
    : [];
  const missing = grant
    ? labelsOf(pipelineVerbsBelowLevel(grant, level), verbs)
    : [];

  return (
    <li>
      <DisclosureRow
        mark={markOf(granted)}
        markLabel={label}
        isDisabled={isDisabled}
        onMark={handleScope}
        // Customer text — the org's own plural label, verbatim.
        title={label}
        summary={describeScope(granted, level, extras, missing)}
      >
        <VerbList
          verbs={verbs}
          level={level}
          scopeLabel={label}
          isDisabled={isDisabled || !granted}
          onToggle={handleVerb}
        />
      </DisclosureRow>
    </li>
  );
}

export interface TunePanelProps {
  grants: AutonomyGrantState;
  derived: AutonomyDerivation;
  caseTypes: AgentCaseType[];
  /** Every pipeline in the org, fetched once; terminal stage names are
   *  selected per case type here rather than one query per row. */
  pipelines: Pipeline[];
  isDisabled: boolean;
  onContactsScope(on: boolean): void;
  onPipelineScope(entityTypeName: string, on: boolean): void;
  onContactsVerb(verb: ContactsVerb, on: boolean): void;
  onPipelineVerb(entityTypeName: string, verb: PipelineVerb, on: boolean): void;
}

export function TunePanel({
  grants,
  derived,
  caseTypes,
  pipelines,
  isDisabled,
  onContactsScope,
  onPipelineScope,
  onContactsVerb,
  onPipelineVerb,
}: TunePanelProps) {
  const terminalNamesByType = useMemo(() => {
    const byType = new Map<string, string[]>();
    for (const t of caseTypes) {
      byType.set(
        t.entityTypeName,
        selectTerminalStageNames(
          pipelines.filter(p => p.entity_type_name === t.entityTypeName)
        )
      );
    }
    return byType;
  }, [caseTypes, pipelines]);

  return (
    <div data-component='autonomy-tune'>
      <p className='mb-2 text-sm text-fg-tertiary'>{TUNE_INTRO}</p>
      <ul className='space-y-0.5'>
        <ContactsScopeRow
          grants={grants}
          level={derived.level}
          isDisabled={isDisabled}
          onScope={onContactsScope}
          onVerb={onContactsVerb}
        />
        {caseTypes.map(caseType => (
          <PipelineScopeRow
            key={caseType.entityTypeName}
            caseType={caseType}
            grant={grants.pipelineGrants[caseType.entityTypeName]}
            terminalNames={
              terminalNamesByType.get(caseType.entityTypeName) ?? []
            }
            level={derived.level}
            isDisabled={isDisabled}
            onScope={onPipelineScope}
            onVerb={onPipelineVerb}
          />
        ))}
      </ul>
      {caseTypes.length > 0 && (
        <p className='mt-2 text-xs text-fg-tertiary'>{NEW_PIPELINES_NOTE}</p>
      )}
    </div>
  );
}
